"use client";

import { useRef } from "react";
import { BUTTON_SMALL } from "@/components/tool-kit/styles";
import { PLANE_FONT } from "@/components/tools/math/coordinate-plane";
import { downloadPng } from "@/lib/tool-kit/download";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

const WIDTH = 480;
const HEIGHT = 300;
const BOX = { left: 70, right: 410, top: 40, bottom: 250 };
const MIN_LEG = 28;
const SQUARE = 16;

export interface DiagramSide {
  /** The side's letter and value as shown: "a = 3". */
  label: string;
  /** Highlight this side as the one that was solved for. */
  highlight?: boolean;
}

/**
 * A right triangle drawn to scale: leg a along the bottom, leg b up the left, the right
 * angle marked at the corner. A leg that would shrink below a visible size is drawn at a
 * minimum length and the caller is told, so the page can say the drawing is not to scale.
 */
export function layoutRightTriangle(a: number, b: number) {
  const scale = Math.min(
    (BOX.right - BOX.left) / a,
    (BOX.bottom - BOX.top) / b
  );
  const width = Math.max(a * scale, MIN_LEG);
  const height = Math.max(b * scale, MIN_LEG);
  const notToScale = a * scale < MIN_LEG || b * scale < MIN_LEG;
  // Centre the triangle in the drawing area.
  const x0 = (WIDTH - width) / 2;
  const y0 = (HEIGHT + height) / 2;
  return { x0, y0, width, height, notToScale };
}

export function RightTriangleDiagram({
  a,
  b,
  sideA,
  sideB,
  sideC,
  title,
  downloadLabel,
}: {
  /** Leg lengths as numbers, for proportions only. */
  a: number;
  b: number;
  sideA: DiagramSide;
  sideB: DiagramSide;
  sideC: DiagramSide;
  title: string;
  downloadLabel: string;
}) {
  const ref = useRef<SVGSVGElement>(null);
  const { x0, y0, width, height } = layoutRightTriangle(a, b);
  const corner = { x: x0, y: y0 };
  const bottomRight = { x: x0 + width, y: y0 };
  const top = { x: x0, y: y0 - height };
  const colorOf = (side: DiagramSide) =>
    side.highlight ? CHART_COLORS[3] : CHART_COLORS[0];
  const weight = (side: DiagramSide) => (side.highlight ? 700 : 500);
  return (
    <div className="space-y-3">
      <svg
        aria-label={title}
        className="h-auto w-full max-w-xl"
        fontFamily={PLANE_FONT}
        ref={ref}
        role="img"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        <title>{title}</title>
        <rect fill={CHART_INK.background} height={HEIGHT} width={WIDTH} />
        <path
          d={`M${corner.x} ${corner.y} L${bottomRight.x} ${bottomRight.y} L${top.x} ${top.y} Z`}
          fill="rgba(0,114,178,0.08)"
          stroke="none"
        />
        <line
          stroke={colorOf(sideA)}
          strokeWidth={3}
          x1={corner.x}
          x2={bottomRight.x}
          y1={corner.y}
          y2={bottomRight.y}
        />
        <line
          stroke={colorOf(sideB)}
          strokeWidth={3}
          x1={corner.x}
          x2={top.x}
          y1={corner.y}
          y2={top.y}
        />
        <line
          stroke={colorOf(sideC)}
          strokeWidth={3}
          x1={bottomRight.x}
          x2={top.x}
          y1={bottomRight.y}
          y2={top.y}
        />
        <path
          d={`M${corner.x + SQUARE} ${corner.y} V${corner.y - SQUARE} H${corner.x}`}
          fill="none"
          stroke={CHART_INK.text}
          strokeWidth={1.5}
        />
        <text
          fill={colorOf(sideA)}
          fontSize={15}
          fontWeight={weight(sideA)}
          textAnchor="middle"
          x={corner.x + width / 2}
          y={corner.y + 26}
        >
          {sideA.label}
        </text>
        <text
          fill={colorOf(sideB)}
          fontSize={15}
          fontWeight={weight(sideB)}
          textAnchor="end"
          x={corner.x - 12}
          y={corner.y - height / 2 + 5}
        >
          {sideB.label}
        </text>
        <text
          fill={colorOf(sideC)}
          fontSize={15}
          fontWeight={weight(sideC)}
          textAnchor="start"
          x={corner.x + width / 2 + 12}
          y={corner.y - height / 2 - 8}
        >
          {sideC.label}
        </text>
      </svg>
      <button
        className={BUTTON_SMALL}
        onClick={() => {
          if (ref.current) {
            downloadPng("right-triangle.png", ref.current).catch(
              () => undefined
            );
          }
        }}
        type="button"
      >
        {downloadLabel}
      </button>
    </div>
  );
}
